import { useState, useEffect } from 'react';

// Premium Leaf Fill Animation - 4 seconds total
export const LeafFillAnimation = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  
  useEffect(() => {
    const duration = 3500; // Fill duration
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const rawProgress = Math.min(elapsed / duration, 1);
      
      // Smooth ease-in-out for natural feel
      const easedProgress = rawProgress < 0.5 
        ? 2 * rawProgress * rawProgress 
        : 1 - Math.pow(-2 * rawProgress + 2, 2) / 2;
      
      setProgress(easedProgress * 100);
      
      if (rawProgress < 1) {
        requestAnimationFrame(animate);
      } else {
        setIsComplete(true);
        if (onComplete) {
          setTimeout(onComplete, 500); // Brief pause before transition
        }
      }
    };
    
    requestAnimationFrame(animate);
  }, [onComplete]);

  // Calculate fill - leaf is 150 units tall (from y=8 to y=158)
  const leafHeight = 150;
  const fillY = 158 - (progress / 100) * leafHeight; // Start from bottom

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-[#FAF8F5] via-[#F5F2EB] to-[#F0EDE6] z-50 overflow-hidden">
      <style>{`
        @keyframes softPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.015); }
        }
        
        @keyframes glowPulse {
          0%, 100% { filter: drop-shadow(0 8px 24px rgba(74, 93, 67, 0.2)); }
          50% { filter: drop-shadow(0 12px 32px rgba(74, 93, 67, 0.3)); }
        }
        
        @keyframes shimmerFlow {
          0% { 
            transform: translateX(-100%) skewX(-15deg); 
            opacity: 0;
          }
          15% { opacity: 0.5; }
          85% { opacity: 0.5; }
          100% { 
            transform: translateX(200%) skewX(-15deg); 
            opacity: 0;
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes logoFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-2px); }
        }
        
        @keyframes completeBounce {
          0% { transform: scale(0.8); opacity: 0; }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }
        
        .leaf-animate {
          animation: softPulse 4s ease-in-out infinite, glowPulse 4s ease-in-out infinite;
        }
        
        .shimmer-flow {
          animation: shimmerFlow 2.8s ease-in-out infinite;
        }
        
        .fade-in {
          animation: fadeIn 0.6s ease-out forwards;
        }
        
        .logo-float {
          animation: logoFloat 3s ease-in-out infinite;
        }
        
        .complete-bounce {
          animation: completeBounce 0.4s ease-out forwards;
        }
      `}</style>
      
      {/* Background particles */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-[#4A5D43]/10"
            style={{
              left: `${20 + i * 15}%`,
              top: `${25 + (i % 3) * 20}%`,
              animation: `softPulse ${3.5 + i * 0.3}s ease-in-out infinite`,
              animationDelay: `${i * 0.2}s`
            }}
          />
        ))}
      </div>
      
      {/* Leaf Container */}
      <div className={`relative ${isComplete ? '' : 'leaf-animate'}`} style={{ width: '130px', height: '180px' }}>
        <svg viewBox="0 0 120 165" className="w-full h-full">
          <defs>
            {/* Leaf background gradient */}
            <linearGradient id="leafBg" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#D4E5CC"/>
              <stop offset="50%" stopColor="#C5DBBA"/>
              <stop offset="100%" stopColor="#D4E5CC"/>
            </linearGradient>
            
            {/* Fill gradient - rich green from bottom */}
            <linearGradient id="fillGradient" x1="0%" y1="100%" x2="0%" y2="0%">
              <stop offset="0%" stopColor="#3D6B32"/>
              <stop offset="30%" stopColor="#4A7A3D"/>
              <stop offset="60%" stopColor="#5A8A4A"/>
              <stop offset="100%" stopColor="#6A9A58"/>
            </linearGradient>
            
            {/* Shimmer gradient */}
            <linearGradient id="shimmer" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(255,255,255,0)"/>
              <stop offset="50%" stopColor="rgba(255,255,255,0.4)"/>
              <stop offset="100%" stopColor="rgba(255,255,255,0)"/>
            </linearGradient>
            
            {/* Leaf shape for clipping */}
            <clipPath id="leafClip">
              <path d="
                M60 8
                C38 8 22 26 14 50
                C6 74 4 100 8 122
                C12 144 28 156 48 160
                C54 161 57 162 60 162
                C63 162 66 161 72 160
                C92 156 108 144 112 122
                C116 100 114 74 106 50
                C98 26 82 8 60 8
                Z
              "/>
            </clipPath>
          </defs>
          
          {/* Shadow */}
          <ellipse cx="60" cy="163" rx="22" ry="2" fill="rgba(44,51,41,0.06)"/>
          
          {/* Leaf outline/background */}
          <path 
            d="M60 8 C38 8 22 26 14 50 C6 74 4 100 8 122 C12 144 28 156 48 160 C54 161 57 162 60 162 C63 162 66 161 72 160 C92 156 108 144 112 122 C116 100 114 74 106 50 C98 26 82 8 60 8 Z"
            fill="url(#leafBg)"
            stroke="#B8D4A8"
            strokeWidth="1"
          />
          
          {/* Fill - animated from bottom */}
          <g clipPath="url(#leafClip)">
            <rect 
              x="0" 
              y={fillY}
              width="120" 
              height="160"
              fill="url(#fillGradient)"
            />
          </g>
          
          {/* Central vein */}
          <path 
            d="M60 158 Q60 120 60 80 Q60 50 60 15" 
            stroke="#3A5F30" 
            strokeWidth="1.5" 
            strokeOpacity="0.4"
            strokeLinecap="round"
            fill="none"
          />
          
          {/* Side veins */}
          <g opacity="0.25" stroke="#3A5F30" strokeWidth="0.8" fill="none" strokeLinecap="round">
            <path d="M60 35 Q45 43 25 48"/>
            <path d="M60 55 Q42 63 20 70"/>
            <path d="M60 75 Q44 83 22 92"/>
            <path d="M60 95 Q46 103 28 114"/>
            <path d="M60 115 Q50 123 38 134"/>
            <path d="M60 135 Q54 142 48 150"/>
            
            <path d="M60 35 Q75 43 95 48"/>
            <path d="M60 55 Q78 63 100 70"/>
            <path d="M60 75 Q76 83 98 92"/>
            <path d="M60 95 Q74 103 92 114"/>
            <path d="M60 115 Q70 123 82 134"/>
            <path d="M60 135 Q66 142 72 150"/>
          </g>
          
          {/* Shimmer effect */}
          <g clipPath="url(#leafClip)">
            <rect 
              x="-40" 
              y="0" 
              width="50" 
              height="170" 
              fill="url(#shimmer)"
              className="shimmer-flow"
            />
          </g>
          
          {/* Edge highlight */}
          <path 
            d="M25 40 Q18 65 18 95 Q20 125 32 145"
            stroke="rgba(255,255,255,0.15)" 
            strokeWidth="2"
            strokeLinecap="round"
            fill="none"
          />
        </svg>
        
        {/* Completion checkmark */}
        {isComplete && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-10 h-10 rounded-full bg-white/95 flex items-center justify-center shadow-lg complete-bounce">
              <svg className="w-5 h-5 text-[#4A5D43]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
          </div>
        )}
      </div>
      
      {/* Logo */}
      <div className="mt-5 fade-in logo-float" style={{ animationDelay: '0.1s' }}>
        <div className="relative">
          <div className="absolute inset-0 bg-[#FAF8F5] rounded-full scale-110 -z-10"/>
          <img
            src="/icons/mart-logo-full.jpg"
            alt="M'ART"
            className="h-12 w-12 object-cover rounded-full border border-[#E8E4DC] shadow-sm"
          />
        </div>
      </div>
      
      {/* Text */}
      <p className="mt-2 text-xs text-[#7A756E] tracking-widest fade-in" style={{ animationDelay: '0.2s' }}>
        דאודורנט טבעי
      </p>
      
      {/* Progress */}
      <div className="mt-4 fade-in" style={{ animationDelay: '0.25s' }}>
        <div className="w-20 h-[3px] bg-[#E8E4DC] rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#4A5D43] to-[#6B9B5A] rounded-full"
            style={{ 
              width: `${progress}%`,
              transition: 'width 0.1s ease-out'
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default LeafFillAnimation;
